import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Empty, Select, Spin, Table, Tag } from 'antd-v5';
import { LoadingOutlined } from '@ant-design/icons';
import { useHistory, Redirect } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import ReportSelectDate from '../../../../CustomizedAntDesign/ReportSelectDate';
import { useEvents } from '../../../../../hooks/query/useEvents';
import usePlanAndLDFlag from '../../../../../hooks/usePlanAndLDFlag';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const isEventInProgress = event => {
  const now = dayjs();
  if (!event.startDate || !event.endDate) return false;
  return dayjs(event.startDate).isSameOrBefore(now, 'day') && dayjs(event.endDate).isSameOrAfter(now, 'day');
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate) return 'No dates set';
  const start = dayjs(startDate);
  const end = endDate ? dayjs(endDate) : null;

  if (!end || start.isSame(end, 'day')) {
    return start.format('ddd MMM D, YYYY');
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('ddd MMM D')} - ${end.format('ddd MMM D, YYYY')}`;
  }
  return `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
};

const AllEvents = ({ auth }) => {
  const history = useHistory();
  const eventsEnabled = usePlanAndLDFlag('events');
  const isAssociate = auth.role === 'ASSOCIATE';
  const stores = useMemo(() => auth?.stores?.filter(store => store.id !== 0) || [], [auth?.stores]);
  const [storeId, setStoreId] = useState(isAssociate ? auth.storeId : (auth.selectedStore || stores[0]?.id || null));
  const [dateRange, setDateRange] = useState([
    dayjs.utc().startOf('year').startOf('day').toDate(),
    dayjs.utc().endOf('year').endOf('day').toDate(),
  ]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleOnDateRangeChange = dates => {
    if (dates.length === 2) {
      const utcStart = new Date(dates[0].getUTCFullYear(), dates[0].getUTCMonth(), dates[0].getUTCDate());
      const utcEnd = new Date(dates[1].getUTCFullYear(), dates[1].getUTCMonth(), dates[1].getUTCDate());
      setDateRange([utcStart, utcEnd]);
      setPage(1);
    }
  };

  const queryParams = useMemo(() => {
    const params = {
      offset: (page - 1) * pageSize,
      limit: pageSize,
    };
    if (isAssociate && auth.storeId) {
      params.storeIds = String(auth.storeId);
    } else if (storeId) {
      params.storeIds = String(storeId);
    }
    if (dateRange[0]) {
      params.fromDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
    }
    if (dateRange[1]) {
      params.toDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
    }
    return params;
  }, [storeId, dateRange, page, pageSize, isAssociate, auth.storeId]);

  const { data: eventsData, isLoading } = useEvents({
    query: queryParams,
    useQueryOptions: {
      staleTime: 30 * 1000,
      enabled: !!auth.merchantId,
    },
  });

  const events = useMemo(() => {
    const rawEvents = eventsData?.data || eventsData || [];

    // Sort by startDate (latest first)
    return [...rawEvents].sort((a, b) => {
      const aDate = a.startDate ? dayjs(a.startDate) : null;
      const bDate = b.startDate ? dayjs(b.startDate) : null;

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1; // Events without dates go to the end
      if (!bDate) return -1;

      return bDate.valueOf() - aDate.valueOf();
    });
  }, [eventsData]);

  const total = eventsData?.total || events.length;

  const handleRowClick = record => {
    history.push(`/Events/event-details/${record.id}`);
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{text || 'Untitled Event'}</span>
          {isEventInProgress(record) && <Tag color="success">In Progress</Tag>}
        </div>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      render: (_, record) => formatDateRange(record.startDate, record.endDate),
    },
    {
      title: 'Stores',
      key: 'stores',
      render: (_, record) => {
        const sessionStores = record.sessions?.flatMap(s => s.stores || []) || [];
        let uniqueStores = [...new Map(sessionStores.map(s => [s.id, s])).values()];
        if (uniqueStores.length === 0) {
          const sessionStoreIds = record.sessions?.flatMap(s => s.storeIds || []) || [];
          const uniqueIds = [...new Set(sessionStoreIds)];
          if (uniqueIds.length > 0) {
            const storeMap = new Map((stores || []).map(s => [s.id, s]));
            uniqueStores = uniqueIds.map(id => storeMap.get(id)).filter(Boolean);
          }
        }
        if (uniqueStores.length === 0) return '-';
        if (uniqueStores.length <= 2) return uniqueStores.map(s => s.name).join(', ');
        return `${uniqueStores[0].name} +${uniqueStores.length - 1} more`;
      },
    },
  ];

  if (!eventsEnabled) {
    return <Redirect to="/" />;
  }

  return (
    <StandardLayoutV2
      title="Events"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Events' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <div className="flex-row-spacebetween-nowrap w-100-P m-btm-15">
        <div className="flex-row-center" style={{ gap: '16px' }}>
          <ReportSelectDate
            onChange={handleOnDateRangeChange}
            defaultValue="thisyear"
            type="custom-options"
            customOptions={[
              { label: 'Today', value: 'today' },
              { label: 'Next 7 days', value: 'next7days' },
              { label: 'This Month', value: 'this-month' },
              { label: 'This Year', value: 'thisyear' },
              { label: 'Custom date range', value: 'custom' },
            ]}
          />
          {!isAssociate && stores.length > 1 && (
            <Select
              options={stores.map(store => ({ label: store.name, value: store.id }))}
              placeholder="Select Store"
              defaultValue={auth.selectedStore || stores[0]?.id || null}
              value={storeId}
              onChange={value => {
                setStoreId(value);
                setPage(1);
              }}
              popupMatchSelectWidth={false}
            />
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="w-100-P flex-col-center m-top-10">
          <Spin tip="Loading..." size="large" indicator={<LoadingOutlined spin />}>
            <div
              style={{
                padding: 50,
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </Spin>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          size="middle"
          bordered={false}
          pagination={{
            pageSize,
            current: page,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: { showSearch: false },
          }}
          onRow={record => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events found" />,
          }}
          loading={isLoading}
        />
      )}
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllEvents);
